import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle } from 'lucide-react';
import EmptyState from '../common/EmptyState';
import ResolveForm from './ResolveForm';
import DisputeList from './DisputeList';
import { AdminPaymentsAPI } from '../../services/api';
import { formatRand, formatDateTime, apiErrorMessage } from '../charter/charterUtils';

const TABS = [
  { id: 'resolve', label: 'Payments to resolve' },
  { id: 'disputes', label: 'Disputes' },
];

// Zuther: `charge` is a charter hire entry (a deposit, the full price or the
// balance); a charter refund is `refund` with `lane: charter` (PR 12b).
const TYPE_LABELS = { authorization: 'Card hold', capture: 'Capture', release: 'Release', refund: 'Refund', tip: 'Tip', card_check: 'Card check', charge: 'Charter charge' };

function verifyText(row) {
  const v = row.lastVerify;
  if (!v) return 'Not verified yet';
  // A charter hire entry keeps only when it was last asked, not the answer (PR 12b).
  const said = v.gatewayStatus || v.status;
  return said ? `${said}, ${formatDateTime(v.at)}` : `Asked ${formatDateTime(v.at)}`;
}

function bookingText(row) {
  if (row.lane === 'charter' && row.bookingCode) return `Hire ${row.bookingCode}`;
  if (row.ride) return `Ride ${row.ride}`;
  if (row.trip) return `Trip ${row.trip}`;
  return '';
}

const Table = ({ headings, children }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>{headings.map((h) => <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-gray-100">{children}</tbody>
    </table>
  </div>
);

/**
 * "Payments to resolve" (Paystack plan 3.7.9): every payment the gateway has
 * not settled after an hour, refunds that wait for the traveller's bank
 * details at Paystack, and the newest founder alerts. Reconciliation checks
 * each stuck row every 10 minutes; a super admin resolves one by hand once
 * the Paystack dashboard shows what happened.
 */
const ToResolve = ({ isSuperAdmin }) => {
  const [data, setData] = useState({ stuck: [], needsAttention: [], alerts: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await AdminPaymentsAPI.toResolve();
      setData(res.data.data);
      setError('');
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not load the payments to resolve'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const done = async (text) => {
    setOpen(null);
    setMessage(text);
    await load();
  };

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading...</div>;

  return (
    <div className="space-y-8">
      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
      {message && <p className="text-sm text-emerald-700" role="status">{message}</p>}

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">In flight for over an hour</h2>
        <p className="text-sm text-gray-500 mb-3">The gateway still cannot say what happened. Check each one in the Paystack dashboard.</p>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {data.stuck.length === 0 && <EmptyState title="Nothing to resolve" message="Every payment has settled." icon={CheckCircle} />}
          {data.stuck.length > 0 && (
            <Table headings={['Payment', 'What', 'Amount', 'Gateway', 'In flight since', 'Last verify', '']}>
              {data.stuck.map((row) => (
                <React.Fragment key={row.reference}>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs text-gray-900">{row.reference}<div className="font-sans text-gray-500">{bookingText(row)}</div></td>
                    <td className="px-4 py-3 text-gray-700">
                      {TYPE_LABELS[row.type] || row.type}
                      {row.captureFailed && <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-800">capture_failed</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-900 whitespace-nowrap">{formatRand(row.amountCents)}</td>
                    <td className="px-4 py-3 text-gray-700">{row.gateway}{row.gatewayEnv ? ` ${row.gatewayEnv}` : ''}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatDateTime(row.createdAt)}</td>
                    <td className="px-4 py-3 text-gray-700">{verifyText(row)}</td>
                    <td className="px-4 py-3 text-right">
                      {isSuperAdmin && open !== row.reference && (
                        <button onClick={() => { setOpen(row.reference); setMessage(''); }} className="px-3 py-1.5 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700">
                          Resolve
                        </button>
                      )}
                    </td>
                  </tr>
                  {open === row.reference && (
                    <tr><td colSpan={7} className="px-4 pb-4"><ResolveForm row={row} onDone={done} onCancel={() => setOpen(null)} /></td></tr>
                  )}
                </React.Fragment>
              ))}
            </Table>
          )}
        </div>
        {!isSuperAdmin && data.stuck.length > 0 && <p className="mt-2 text-sm text-gray-500">Only a super admin can resolve a payment.</p>}
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Refunds waiting for bank details</h2>
        <p className="text-sm text-gray-500 mb-3">Paystack needs the traveller's bank details. Retry each with the customer details in the Paystack dashboard.</p>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {data.needsAttention.length === 0 && <div className="p-4 text-sm text-gray-500">None.</div>}
          {data.needsAttention.length > 0 && (
            <Table headings={['Refund', 'Amount', 'Since']}>
              {data.needsAttention.map((row) => (
                <tr key={row.reference}>
                  <td className="px-4 py-3 font-mono text-xs text-gray-900">{row.reference}</td>
                  <td className="px-4 py-3 text-gray-900">{formatRand(row.amountCents)}</td>
                  <td className="px-4 py-3 text-gray-700">{formatDateTime(row.createdAt)}</td>
                </tr>
              ))}
            </Table>
          )}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Alerts, last 30 days</h2>
        <p className="text-sm text-gray-500 mb-3">Each alert fires once. It emails the founders unless every lane runs in simulation.</p>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {data.alerts.length === 0 && <div className="p-4 text-sm text-gray-500">No alerts.</div>}
          {data.alerts.length > 0 && (
            <Table headings={['When', 'Alert', 'Emailed']}>
              {data.alerts.map((a) => (
                <tr key={a.key}>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap align-top">{formatDateTime(a.createdAt)}</td>
                  <td className="px-4 py-3 text-gray-900">{a.message}</td>
                  <td className="px-4 py-3 text-gray-700 align-top">{a.emailedAt ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </Table>
          )}
        </div>
      </section>
    </div>
  );
};

const PaymentsPage = ({ isSuperAdmin }) => {
  const [tab, setTab] = useState('resolve');
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
        <p className="text-sm text-gray-500">What reconciliation could not settle, and card disputes.</p>
      </div>
      <div className="flex flex-wrap gap-2 mb-6" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium ${tab === t.id ? 'bg-emerald-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'resolve' ? <ToResolve isSuperAdmin={isSuperAdmin} /> : <DisputeList />}
    </div>
  );
};

export default PaymentsPage;
